/**
 * «Утилиты» кассы (D-40, этап 4, DD6): что проверяется по кабелю и как это
 * показывать.
 *
 * Касса отдаёт значения и факты («Найти iPhone» включено, SMART провален).
 * Тревогу по порогу ставит кабинет: порог — настройка платформы, а не число в
 * агенте, и меняется без выпуска образа.
 *
 * Общий для сервера и экрана: сервер отдаёт пороги с билетом и проверяет вид
 * отчёта, экран раскладывает поля по важности.
 */

export const CABLE_TOOLS = ['iphone', 'android', 'camera', 'disk'] as const
export type CableTool = (typeof CABLE_TOOLS)[number]

export const isCableTool = (value: unknown): value is CableTool =>
  typeof value === 'string' && (CABLE_TOOLS as readonly string[]).includes(value)

/**
 * На что сервер выдаёт билет к агенту кассы: утилиты, «что подключено» (E3,
 * окно «Подключён iPhone — проверить?») и установка приложения телефона точки
 * кабелем (фаза 7). Список тот же, что у агента (`ToolTicket.Known`)
 */
export const TICKET_TOOLS = [...CABLE_TOOLS, 'watch', 'provision'] as const
export type TicketTool = (typeof TICKET_TOOLS)[number]

export const isTicketTool = (value: unknown): value is TicketTool =>
  typeof value === 'string' && (TICKET_TOOLS as readonly string[]).includes(value)

/** Хэш серийника USB от агента: 32 шестнадцатеричных знака */
export const isSerialHash = (value: unknown): value is string => typeof value === 'string' && /^[0-9a-f]{32}$/.test(value)

/** Ответ агента кассы (`Tools/ToolReport.cs`) */
export interface ToolReport {
  state: 'ok' | 'no_device' | 'untrusted' | 'unsupported' | 'busy' | 'many' | 'error'
  model: string | null
  imei: string | null
  values: Record<string, string> | null
  alerts: string[] | null
  message: string | null
  /** Подпись кассы: без неё сервер отчёт в учёт не примет (`reports.post.ts`) */
  signed?: string | null
}

export interface ToolThresholds {
  /** Батарея ниже — тревога, % от новой */
  batteryMin: number
  /** Ресурс SSD израсходован больше — тревога, % */
  ssdWearMax: number
}

export const TOOL_THRESHOLD_LIMITS = { min: 1, max: 100 } as const

export const DEFAULT_TOOL_THRESHOLDS: ToolThresholds = { batteryMin: 80, ssdWearMax: 80 }

/** Поля порогов в мастер-кабинете: колонка базы и ключ (подписи — в словаре, `workplaces.threshold.*`) */
export const TOOL_THRESHOLD_FIELDS = [
  { column: 'tool_battery_min', key: 'batteryMin' },
  { column: 'tool_ssd_wear_max', key: 'ssdWearMax' },
] as const

/**
 * Поля по важности (DD6): вторая ступень — что это за вещь, третья — цифры
 * состояния, остальное — под раскрытием. Поле из списка, которого нет в отчёте,
 * показывается как «не удалось прочитать», а не пропадает.
 */
export const TOOL_FIELDS: Record<CableTool, { main: string[]; numbers: string[]; rest: string[] }> = {
  iphone: { main: ['storageGb'], numbers: ['batteryHealth', 'batteryCycles'], rest: ['ios', 'modelNumber'] },
  android: { main: ['storageGb'], numbers: ['batteryLevel', 'batteryCycles'], rest: ['android'] },
  camera: { main: [], numbers: ['shutterCount'], rest: [] },
  disk: { main: ['type', 'sizeGb'], numbers: ['smart', 'powerOnHours'], rest: ['temperature', 'wearPercent'] },
}

/** У каких утилит есть IMEI: для остальных строки IMEI нет вовсе */
export const HAS_IMEI: Record<CableTool, boolean> = { iphone: true, android: true, camera: false, disk: false }

/**
 * Хвост комментария отметки, поставленной кабелем: по нему запись паспорта
 * узнаёт свою прошлую отметку (обновить) и отличает её от отметки человека (не трогать)
 */
export const CABLE_MARK = '— проверено по кабелю'

/** Отметка «Паспорта проверки» (X3), которую даёт отчёт с кабеля (E2) */
export interface CableInspection {
  /** Пункт чек-листа (`services/categories/inspection.ts`) */
  key: string
  result: 'ok' | 'defect'
  comment: string
}

/**
 * Какой пункт паспорта проверки закрывает отчёт с кабеля (D-40, E2) — только то,
 * что судится по цифрам и порогу-настройке. iPhone — «Аккумулятор» по ёмкости
 * от новой; диск — «Накопитель» по SMART и износу. Android отдаёт заряд, а не
 * износ, у затвора общего порога нет (норма у каждой модели своя) — их пункты
 * ставит человек. Не прочиталось — `null`, а не «в порядке».
 */
export function inspectionFromReport(
  tool: CableTool,
  values: Record<string, string>,
  thresholds: ToolThresholds,
): CableInspection | null {
  if (tool === 'iphone') {
    const health = Number(values.batteryHealth)
    if (values.batteryHealth === undefined || !Number.isFinite(health)) return null
    const cycles = Number(values.batteryCycles)
    const tail = values.batteryCycles !== undefined && Number.isFinite(cycles) ? `, циклов зарядки: ${cycles}` : ''
    return {
      key: 'battery',
      result: health >= thresholds.batteryMin ? 'ok' : 'defect',
      comment: `Ёмкость аккумулятора ${health}% от новой${tail} ${CABLE_MARK}`,
    }
  }
  if (tool === 'disk') {
    if (values.smart !== 'passed' && values.smart !== 'failed') return null
    const wear = Number(values.wearPercent)
    const worn = values.wearPercent !== undefined && Number.isFinite(wear) && wear > thresholds.ssdWearMax
    const parts = [
      values.smart === 'passed' ? 'Самодиагностика SMART пройдена' : 'Самодиагностика SMART не пройдена',
      values.wearPercent !== undefined && Number.isFinite(wear) ? `ресурс израсходован на ${wear}%` : null,
    ].filter(Boolean)
    return {
      key: 'storage',
      result: values.smart === 'passed' && !worn ? 'ok' : 'defect',
      comment: `${parts.join(', ')} ${CABLE_MARK}`,
    }
  }
  return null
}

/** Тревоги по порогам — к фактам кассы, после них: факт («Найти iPhone») важнее износа */
export function thresholdAlerts(tool: CableTool, values: Record<string, string>, thresholds: ToolThresholds): string[] {
  const out: string[] = []
  const battery = Number(values.batteryHealth)
  if (tool === 'iphone' && values.batteryHealth !== undefined && Number.isFinite(battery) && battery < thresholds.batteryMin) {
    out.push(`Батарея изношена: ${battery}% от новой`)
  }
  const wear = Number(values.wearPercent)
  if (tool === 'disk' && values.wearPercent !== undefined && Number.isFinite(wear) && wear > thresholds.ssdWearMax) {
    out.push(`Ресурс SSD израсходован на ${wear}%`)
  }
  return out
}
